import { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { WeeklyPlan, WeeklyGoal, DayTheme } from '@/types';
import { firebaseUserAtom } from '@/stores/auth';
import { queryKeys } from '@/lib/queryKeys';
import {
  getWeeklyPlan,
  createWeeklyPlan,
  updateWeeklyPlanGoals,
  updateWeeklyPlanDayTheme,
  updateWeeklyPlanNotes,
  subscribeToWeeklyPlan,
} from '@/repositories/weekly-plan.repository';
import { generateGoalId } from '@/services/weekly-plan.service';

// Re-export date utilities for backward compatibility
export { getWeekId, getWeekBounds, getWeekDates } from '@/services/weekly-plan.service';

export interface UseWeeklyPlanOptions {
  weekId: string;
}

export interface UseWeeklyPlanResult {
  weeklyPlan: WeeklyPlan | null;
  isLoading: boolean;
  error: Error | null;
  getOrCreateWeeklyPlan: () => Promise<WeeklyPlan | null>;
  updateGoals: (goals: WeeklyGoal[]) => Promise<boolean>;
  setDayTheme: (date: string, theme: DayTheme) => Promise<boolean>;
  updateNotes: (notes: string) => Promise<boolean>;
  toggleGoalCompletion: (goalId: string) => Promise<boolean>;
  addGoal: (title: string) => Promise<boolean>;
  removeGoal: (goalId: string) => Promise<boolean>;
  reorderGoals: (goals: WeeklyGoal[]) => Promise<boolean>;
}

export function useWeeklyPlan({ weekId }: UseWeeklyPlanOptions): UseWeeklyPlanResult {
  const firebaseUser = useAtomValue(firebaseUserAtom);
  const queryClient = useQueryClient();
  const [isListenerInitialized, setIsListenerInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const prevKeyRef = useRef<string | null>(null);

  const userId = firebaseUser?.uid;
  const queryKey = useMemo(() => queryKeys.weeklyPlan.byWeek(userId, weekId), [userId, weekId]);
  const cacheKey = `${userId}-${weekId}`;

  const { data: weeklyPlan = null } = useQuery<WeeklyPlan | null, Error>({
    queryKey,
    queryFn: async () => null,
    enabled: !!userId,
    staleTime: Infinity,
  });

  // Real-time listener
  useEffect(() => {
    if (prevKeyRef.current !== cacheKey) {
      prevKeyRef.current = cacheKey;
      setIsListenerInitialized(false);
      setError(null);
    }
    if (!userId) {
      setIsListenerInitialized(true);
      return;
    }

    let isCleanedUp = false;
    const timeoutId = setTimeout(() => {
      if (!isCleanedUp && !isListenerInitialized) setIsListenerInitialized(true);
    }, 2000);

    const unsubscribe = subscribeToWeeklyPlan(
      userId,
      weekId,
      (plan) => {
        if (!isCleanedUp) {
          queryClient.setQueryData(queryKey, plan);
          setIsListenerInitialized(true);
        }
      },
      (err) => {
        if (!isCleanedUp) {
          setError(err);
          setIsListenerInitialized(true);
        }
      }
    );

    return () => {
      isCleanedUp = true;
      clearTimeout(timeoutId);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const getOrCreateWeeklyPlan = useCallback(async (): Promise<WeeklyPlan | null> => {
    if (!userId) return null;
    try {
      const existing = await getWeeklyPlan(userId, weekId);
      if (existing) return existing;
      const created = await createWeeklyPlan(userId, weekId);
      queryClient.setQueryData(queryKey, created);
      return created;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
      return null;
    }
  }, [userId, weekId, queryClient, queryKey]);

  const optimisticUpdate = useCallback(
    async (
      updater: (plan: WeeklyPlan) => WeeklyPlan,
      operation: () => Promise<void>
    ): Promise<boolean> => {
      if (!userId) return false;
      const current = queryClient.getQueryData<WeeklyPlan>(queryKey);
      if (current) queryClient.setQueryData(queryKey, updater(current));
      try {
        await getOrCreateWeeklyPlan();
        await operation();
        return true;
      } catch (error) {
        console.error('Weekly plan operation failed:', error);
        if (current) queryClient.setQueryData(queryKey, current);
        return false;
      }
    },
    [userId, queryClient, queryKey, getOrCreateWeeklyPlan]
  );

  const updateGoals = useCallback(
    (goals: WeeklyGoal[]) =>
      optimisticUpdate(
        (p) => ({ ...p, goals }),
        () => updateWeeklyPlanGoals(userId!, weekId, goals)
      ),
    [userId, weekId, optimisticUpdate]
  );

  const setDayTheme = useCallback(
    (date: string, theme: DayTheme) =>
      optimisticUpdate(
        (p) => ({ ...p, dayThemes: { ...p.dayThemes, [date]: theme } }),
        async () => {
          const plan = queryClient.getQueryData<WeeklyPlan>(queryKey);
          await updateWeeklyPlanDayTheme(userId!, weekId, { ...plan?.dayThemes, [date]: theme });
        }
      ),
    [userId, weekId, queryClient, queryKey, optimisticUpdate]
  );

  const updateNotes = useCallback(
    (notes: string) =>
      optimisticUpdate(
        (p) => ({ ...p, notes }),
        () => updateWeeklyPlanNotes(userId!, weekId, notes)
      ),
    [userId, weekId, optimisticUpdate]
  );

  const toggleGoalCompletion = useCallback(
    async (goalId: string): Promise<boolean> => {
      const plan = queryClient.getQueryData<WeeklyPlan>(queryKey);
      const idx = plan?.goals.findIndex((g) => g.id === goalId) ?? -1;
      if (!plan || idx === -1) return false;
      const goals = plan.goals.map((g, i) => (i === idx ? { ...g, completed: !g.completed } : g));
      return updateGoals(goals);
    },
    [queryClient, queryKey, updateGoals]
  );

  const addGoal = useCallback(
    async (title: string): Promise<boolean> => {
      const plan = queryClient.getQueryData<WeeklyPlan>(queryKey);
      const newGoal: WeeklyGoal = {
        id: generateGoalId(),
        title: title.trim(),
        completed: false,
        order: plan?.goals.length ?? 0,
      };
      return updateGoals([...(plan?.goals || []), newGoal]);
    },
    [queryClient, queryKey, updateGoals]
  );

  const removeGoal = useCallback(
    async (goalId: string): Promise<boolean> => {
      const plan = queryClient.getQueryData<WeeklyPlan>(queryKey);
      if (!plan || !plan.goals.some((g) => g.id === goalId)) return false;
      return updateGoals(plan.goals.filter((g) => g.id !== goalId));
    },
    [queryClient, queryKey, updateGoals]
  );

  const reorderGoals = useCallback(
    (goals: WeeklyGoal[]) => updateGoals([...goals].sort((a, b) => a.order - b.order)),
    [updateGoals]
  );

  return {
    weeklyPlan,
    isLoading: !!userId && !isListenerInitialized && !weeklyPlan,
    error,
    getOrCreateWeeklyPlan,
    updateGoals,
    setDayTheme,
    updateNotes,
    toggleGoalCompletion,
    addGoal,
    removeGoal,
    reorderGoals,
  };
}
